import { useTranslation } from 'react-i18next';
import { Check, Coins, Loader2, Zap } from 'lucide-react';
import { CheckoutStrip } from '@/components/trust/CheckoutStrip';
import { mxn, perCredit, usd, type PassOffer } from '@/lib/studio-pass';
import { cn } from '@/lib/utils';

// THE STUDIO PASS CARD: the monthly offer as one glass tile in the site's
// look (gold rim, Syne price, amber pay button with its amount on it, the
// checkout strip under it). Used by /pass; the pay sheet and /pricing can
// place it beside the packs. Draws nothing while the pass is off, unless
// `showWhenOff` (the /pass page, which says "coming soon").
//
// Money rule: the button spends money, so it is amber and shows the amount.
export function StudioPassCard({
  offer,
  onJoin,
  busy = false,
  showWhenOff = false,
  className,
}: {
  offer: PassOffer;
  onJoin?: () => void;
  busy?: boolean;
  showWhenOff?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  if (!offer.enabled && !showWhenOff) return null;
  const price = mxn(offer.price_cents);
  const perks = [
    t('pass.card.perkCredits', { credits: offer.credits.toLocaleString('en-US') }),
    ...(offer.priority ? [t('pass.card.perkPriority')] : []),
    t('pass.card.perkKeep'),
    t('pass.card.perkCancel'),
  ];

  return (
    <article
      data-testid="studio-pass-card"
      className={cn(
        'hero-hairline relative overflow-hidden rounded-[1.35rem] border border-primary/45 bg-[linear-gradient(180deg,rgba(244,190,82,0.10),rgba(255,255,255,0.02)_45%,rgba(255,255,255,0.015))] p-5 shadow-[0_0_48px_-12px_hsl(var(--primary)/0.35),0_24px_48px_-28px_rgba(0,0,0,0.8)] sm:p-7',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[12px] font-semibold uppercase tracking-[0.18em] text-primary">{t('pass.card.eyebrow')}</p>
          <h2 className="mt-1 font-display text-[26px] font-bold leading-tight sm:text-[30px]">{t('pass.name')}</h2>
        </div>
        <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/[0.12] text-primary ring-1 ring-inset ring-primary/25">
          <Zap className="h-5 w-5" aria-hidden />
        </span>
      </div>

      <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1 tabular-nums">
        <span className="whitespace-nowrap">
          <span className="font-display text-[40px] font-extrabold leading-none" data-testid="pass-mxn">{price}</span>
          <span className="ml-1 text-sm font-bold text-muted-foreground">MXN</span>
        </span>
        <span className="whitespace-nowrap">
          <span className="text-[22px] font-bold text-foreground/85" data-testid="pass-usd">{usd(offer.usd_cents)}</span>
          <span className="ml-1 text-sm font-bold text-muted-foreground">USD</span>
        </span>
        <span className="text-[15px] text-muted-foreground">{t('pass.card.perMonth')}</span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-[14px] font-semibold text-primary tabular-nums" data-testid="pass-credits">
          <Coins className="h-4 w-4" aria-hidden />
          {t('pass.card.creditsChip', { credits: offer.credits.toLocaleString('en-US') })}
        </span>
        <span className="text-[13px] text-muted-foreground tabular-nums">{t('pass.card.rate', { rate: perCredit(offer) })}</span>
      </div>

      <ul className="mt-5 space-y-2.5">
        {perks.map((p) => (
          <li key={p} className="flex items-start gap-2.5 text-[15px] leading-snug">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden />
            <span>{p}</span>
          </li>
        ))}
      </ul>

      {offer.enabled ? (
        <>
          <button
            type="button"
            data-testid="pass-join"
            disabled={busy}
            onClick={onJoin}
            className="mt-6 inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full bg-[linear-gradient(135deg,#F9D27E,#F4BE52_45%,#E4A43A)] px-6 text-[16px] font-bold text-[#10131a] shadow-[0_10px_30px_-10px_rgba(244,190,82,0.7)] transition hover:brightness-105 disabled:opacity-70"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden /> : null}
            {t('pass.card.join', { price })}
          </button>
          <CheckoutStrip methods="card" />
        </>
      ) : (
        <div
          data-testid="pass-coming-soon"
          className="mt-6 inline-flex min-h-[52px] w-full items-center justify-center rounded-full border border-white/15 bg-white/[0.05] px-6 text-[15px] font-semibold text-muted-foreground"
        >
          {t('pass.card.comingSoon')}
        </div>
      )}
    </article>
  );
}
